import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeft, Building2, Pencil, Trash2, X, Save, Plus, FileSignature,
} from 'lucide-react';
import { getProduttore, updateProduttore, deleteProduttore } from '../api/produttori';
import { apiErrorMessage } from '../lib/apiError';
import type { Produttore } from '../types/produttore';
import { useCategorie } from '../hooks/useCategorie';
import { categoriaLabel } from '../lib/categorie';
import { produttoreUpdateSchema, type ProduttoreUpdateData } from '../schemas/produttore';
import type { ProduttoreUpdatePayload } from '../api/produttori';
import { getRichieste, type RichiestaListItem } from '../api/richieste';
import { listOmologhe, deleteOmologa } from '../api/omologhe';
import type { Omologa } from '../types/omologa';
import OmologaModal from '../components/OmologaModal';
import { useAuthStore } from '../store/authStore';
import { Skeleton, SkeletonRow } from '../components/ui/Skeleton';
import Badge from '../components/ui/Badge';
import StatusBadge from '../components/ui/StatusBadge';
import i18n from '../i18n';
import { canWriteAnagraficaCommerciale, canWriteAnagraficaTecnica } from '../lib/constants';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDateIT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
}

function tipoSoggettoBadge(tipo: string | null) {
  if (!tipo) return null;
  const MAP: Record<string, { label: string; variant: 'info' | 'neutral' | 'purple' }> = {
    azienda: { label: i18n.t('anagrafiche.tipo_soggetto.azienda'), variant: 'info' },
    privato: { label: i18n.t('anagrafiche.tipo_soggetto.privato'), variant: 'neutral' },
    ente_pubblico: { label: i18n.t('anagrafiche.tipo_soggetto.ente_pubblico'), variant: 'purple' },
  };
  return MAP[tipo] ?? { label: tipo, variant: 'neutral' as const };
}

// ── Row helper ────────────────────────────────────────────────────────────────

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 py-2.5 border-b border-stone-mid last:border-0 items-start">
      <dt className="text-xs text-ink-soft min-w-[160px] pt-0.5 flex-shrink-0">{label}</dt>
      <dd className="flex-1 text-sm text-ink-dark">{children}</dd>
    </div>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div className="p-8 max-w-5xl mx-auto animate-pulse">
      <Skeleton className="h-4 w-32 mb-6" />
      <div className="flex gap-6">
        <div className="flex-[3] space-y-4">
          <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-stone-mid bg-stone-bg">
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="px-5 py-4 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-4 w-full" />)}
            </div>
          </div>
        </div>
        <div className="flex-[1] space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    </div>
  );
}

// ── Form input styles ─────────────────────────────────────────────────────────

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

function FieldWrap({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 py-1.5 border-b border-stone-mid last:border-0 items-start">
      <label className="text-xs text-ink-soft min-w-[160px] pt-2 flex-shrink-0">{label}</label>
      <div className="flex-1">
        {children}
        {error && <p className="text-xs text-accent-danger mt-0.5">{error}</p>}
      </div>
    </div>
  );
}

// ── Pagina principale ─────────────────────────────────────────────────────────

export default function DettaglioProduttorePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { categorie } = useCategorie();   // R4-A: lookup codice → nome_visualizzato
  const { user } = useAuthStore();

  const userRole = user?.role ?? '';
  const canEdit = canWriteAnagraficaCommerciale(userRole);
  // Scrittura, non lettura: la regola e' quella dei 10 endpoint di omologhe.py.
  const canEditOmologa = canWriteAnagraficaTecnica(userRole);

  const [produttore, setProduttore] = useState<Produttore | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [ordini, setOrdini] = useState<RichiestaListItem[]>([]);
  const [ordiniLoading, setOrdiniLoading] = useState(false);

  // Omologhe del produttore (Blocco 6)
  const [omologhe, setOmologhe] = useState<Omologa[]>([]);
  const [omologheLoading, setOmologheLoading] = useState(false);
  const [omologaModal, setOmologaModal] = useState<{ open: boolean; editing: Omologa | null }>({ open: false, editing: null });
  const [omologaDeleteError, setOmologaDeleteError] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ProduttoreUpdateData>({
    resolver: zodResolver(produttoreUpdateSchema),
  });

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setLoading(true);
      try {
        setProduttore(await getProduttore(id));
      } catch (err) {
        if ((err as { response?: { status?: number } })?.response?.status === 404) setNotFound(true);
        else setFetchError(t('anagrafiche.dett_cliente.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, t]);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setOrdiniLoading(true);
      try {
        setOrdini(await getRichieste({ produttore_id: id }));
      } catch {
        setOrdini([]);
      } finally {
        setOrdiniLoading(false);
      }
    })();
  }, [id]);

  const fetchOmologhe = useCallback(() => {
    if (!id) return;
    void (async () => {
      setOmologheLoading(true);
      try {
        const res = await listOmologhe({ produttore_id: id, limit: 50 });
        setOmologhe(res.items);
      } catch {
        setOmologhe([]);
      } finally {
        setOmologheLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    fetchOmologhe();
  }, [fetchOmologhe]);

  function stateBadgeVariant(stato: string): 'success' | 'danger' | 'neutral' {
    if (stato === 'valida') return 'success';
    if (stato === 'scaduta') return 'danger';
    return 'neutral';
  }

  function statoFirmaBadgeVariant(s: string): 'neutral' | 'info' | 'purple' | 'success' {
    switch (s) {
      case 'generata': return 'info';
      case 'inviata': return 'purple';
      case 'firmata': return 'success';
      default: return 'neutral';
    }
  }

  async function handleDeleteOmologa(omologaId: string) {
    setOmologaDeleteError(null);
    try {
      await deleteOmologa(omologaId);
      fetchOmologhe();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { detail?: string } } })?.response?.status;
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (status === 409) {
        setOmologaDeleteError(detail ?? t('anagrafiche.dett_produttore.omologa_in_uso'));
      } else {
        setOmologaDeleteError(t('anagrafiche.dett_cliente.errore_cancellazione'));
      }
    }
  }

  function startEdit() {
    if (!produttore) return;
    reset({
      ragione_sociale: produttore.ragione_sociale,
      piva: produttore.piva ?? '',
      codice_fiscale: produttore.codice_fiscale ?? '',
      email: produttore.email ?? '',
      telefono: produttore.telefono ?? '',
      indirizzo: produttore.indirizzo ?? '',
      citta: produttore.citta ?? '',
      provincia: produttore.provincia ?? '',
      cap: produttore.cap ?? '',
      note: produttore.note ?? '',
      tipo_soggetto: produttore.tipo_soggetto ?? '',
    });
    setIsEditing(true);
    setSaveError(null);
  }

  function cancelEdit() {
    setIsEditing(false);
    setSaveError(null);
  }

  function buildPayload(data: ProduttoreUpdateData): ProduttoreUpdatePayload {
    const payload: ProduttoreUpdatePayload = {};
    if (data.ragione_sociale !== undefined) payload.ragione_sociale = data.ragione_sociale;
    payload.piva = data.piva?.trim() || null;
    payload.codice_fiscale = data.codice_fiscale?.trim() || null;
    payload.email = data.email?.trim() || null;
    payload.telefono = data.telefono?.trim() || null;
    payload.indirizzo = data.indirizzo?.trim() || null;
    payload.citta = data.citta?.trim() || null;
    payload.provincia = data.provincia?.trim() || null;
    payload.cap = data.cap?.trim() || null;
    payload.note = data.note?.trim() || null;
    const ts = data.tipo_soggetto?.trim();
    payload.tipo_soggetto = (ts as 'azienda' | 'privato' | 'ente_pubblico' | null) || null;
    return payload;
  }

  const onSubmit = async (data: ProduttoreUpdateData) => {
    if (!id) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const updated = await updateProduttore(id, buildPayload(data));
      setProduttore(updated);
      setIsEditing(false);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setSaveError(t('anagrafiche.crea_produttore.piva_esistente'));
      } else {
        // T1 sul CF vale anche qui: il 422 ha un detail parlante (stringa o
        // array Pydantic) — `apiErrorMessage` lo estrae invece di scartarlo.
        setSaveError(apiErrorMessage(err, t('anagrafiche.crea_produttore.errore_salvataggio')));
      }
    } finally {
      setIsSaving(false);
    }
  };

  async function handleDelete() {
    if (!id) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteProduttore(id);
      navigate('/produttori');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { detail?: string } } })?.response?.status;
      if (status === 409) {
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setDeleteError(detail ?? t('anagrafiche.dett_produttore.in_uso'));
      } else {
        setDeleteError(t('anagrafiche.dett_cliente.errore_cancellazione'));
      }
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  }

  // ── Error states ─────────────────────────────────────────────────────────

  if (loading) return <PageSkeleton />;

  if (notFound) {
    return (
      <div className="p-8 text-center">
        <p className="text-2xl font-bold text-ink-dark mb-2">404</p>
        <p className="text-ink-soft mb-4">{t('anagrafiche.dett_produttore.non_trovato')}</p>
        <Link to="/produttori" className="text-sage underline text-sm">{t('anagrafiche.dett_produttore.torna')}</Link>
      </div>
    );
  }

  if (fetchError || !produttore) {
    return (
      <div className="p-8 text-center">
        <p className="text-accent-danger font-medium mb-4">{fetchError ?? t('anagrafiche.dett_cliente.errore_sconosciuto')}</p>
        <button type="button" onClick={() => navigate('/produttori')} className="text-sage underline text-sm">
          {t('anagrafiche.dett_produttore.torna')}
        </button>
      </div>
    );
  }

  const p = produttore;
  const tipo = tipoSoggettoBadge(p.tipo_soggetto);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm mb-6">
          <Link to="/produttori" className="text-sage hover:underline flex items-center gap-1">
            <ArrowLeft size={14} />
            {t('anagrafiche.produttori.titolo')}
          </Link>
          <span className="text-ink-faint">/</span>
          <span className="font-semibold text-ink-dark">{p.ragione_sociale}</span>
        </div>

        <div className="flex gap-6 items-start">
          {/* ── Colonna principale ── */}
          <div className="flex-[3] min-w-0 space-y-4">

            {/* Card dati produttore */}
            <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-stone-mid bg-stone-bg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 size={16} className="text-ink-soft" />
                  <span className="text-sm font-semibold text-ink-dark">{t('anagrafiche.dett_produttore.dati_produttore')}</span>
                </div>
                {canEdit && !isEditing && (
                  <button
                    type="button"
                    onClick={startEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors"
                  >
                    <Pencil size={12} />
                    {t('anagrafiche.dett_cliente.modifica')}
                  </button>
                )}
                {isEditing && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={isSaving}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors"
                    >
                      <X size={12} />
                      {t('common.azioni.annulla')}
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit(onSubmit)}
                      disabled={isSaving}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-sage rounded-md hover:bg-sage-dark transition-colors disabled:opacity-60"
                    >
                      <Save size={12} />
                      {isSaving ? t('anagrafiche.dett_cliente.salvataggio') : t('anagrafiche.dett_cliente.salva')}
                    </button>
                  </div>
                )}
              </div>

              <div className="px-5 py-4">
                {saveError && (
                  <div className="mb-3 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded-md text-sm text-accent-danger">
                    {saveError}
                  </div>
                )}

                {isEditing ? (
                  <dl className="divide-y divide-stone-mid">
                    <FieldWrap label={t('anagrafiche.campi.ragione_sociale')} error={errors.ragione_sociale?.message}>
                      <input className={inputCls} {...register('ragione_sociale')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.partita_iva')} error={errors.piva?.message}>
                      <input className={inputCls} {...register('piva')} maxLength={11} placeholder={t('anagrafiche.crea_cliente.ph_piva')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.codice_fiscale')} error={errors.codice_fiscale?.message}>
                      <input className={inputCls} {...register('codice_fiscale')} maxLength={16} placeholder={t('anagrafiche.crea_cliente.ph_cf')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.tipo_soggetto')} error={errors.tipo_soggetto?.message}>
                      <select className={inputCls} {...register('tipo_soggetto')}>
                        <option value="">{t('anagrafiche.comune.seleziona')}</option>
                        <option value="azienda">{t('anagrafiche.tipo_soggetto.azienda')}</option>
                        <option value="privato">{t('anagrafiche.tipo_soggetto.privato')}</option>
                        <option value="ente_pubblico">{t('anagrafiche.tipo_soggetto.ente_pubblico')}</option>
                      </select>
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.email')} error={errors.email?.message}>
                      <input className={inputCls} type="email" {...register('email')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.telefono')} error={errors.telefono?.message}>
                      <input className={inputCls} {...register('telefono')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.indirizzo')} error={errors.indirizzo?.message}>
                      <input className={inputCls} {...register('indirizzo')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.citta')} error={errors.citta?.message}>
                      <input className={inputCls} {...register('citta')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.provincia')} error={errors.provincia?.message}>
                      <input className={inputCls} {...register('provincia')} maxLength={2} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.cap')} error={errors.cap?.message}>
                      <input className={inputCls} {...register('cap')} maxLength={5} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.note')} error={errors.note?.message}>
                      <textarea className={inputCls} rows={3} {...register('note')} />
                    </FieldWrap>
                  </dl>
                ) : (
                  <dl className="divide-y divide-stone-mid">
                    <InfoRow label={t('anagrafiche.campi.ragione_sociale')}>
                      <span className="font-medium">{p.ragione_sociale}</span>
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.partita_iva')}>
                      {p.piva ? <span className="font-mono">{p.piva}</span> : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.codice_fiscale')}>
                      {p.codice_fiscale ? <span className="font-mono">{p.codice_fiscale}</span> : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.tipo_soggetto')}>
                      {tipo ? (
                        <Badge variant={tipo.variant}>{tipo.label}</Badge>
                      ) : (
                        <span className="text-ink-faint">—</span>
                      )}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.email')}>
                      {p.email ? (
                        <a href={`mailto:${p.email}`} className="text-sage hover:underline">{p.email}</a>
                      ) : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.telefono')}>
                      {p.telefono ? (
                        <a href={`tel:${p.telefono}`} className="text-sage hover:underline">{p.telefono}</a>
                      ) : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.indirizzo')}>
                      {p.indirizzo ? (
                        <span>{p.indirizzo}</span>
                      ) : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.citta')}>
                      {p.citta ? <span>{p.citta}</span> : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.provincia')}>
                      {p.provincia ? <span>{p.provincia}</span> : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.cap')}>
                      {p.cap ? <span className="font-mono">{p.cap}</span> : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.note')}>
                      {p.note ? (
                        <span className="whitespace-pre-line">{p.note}</span>
                      ) : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.dett_cliente.aggiornato_il')}>
                      <span className="text-ink-soft">{formatDateIT(p.updated_at)}</span>
                    </InfoRow>
                  </dl>
                )}
              </div>
            </div>

            {/* Omologhe del produttore (Blocco 6) */}
            <div className="bg-white border border-stone-mid rounded-xl overflow-hidden" data-testid="section-omologhe">
              <div className="px-5 py-3.5 border-b border-stone-mid bg-stone-bg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileSignature size={16} className="text-ink-soft" />
                  <span className="text-sm font-semibold text-ink-dark">
                    {t('anagrafiche.dett_produttore.omologhe')} {!omologheLoading && `(${omologhe.length})`}
                  </span>
                </div>
                {canEditOmologa && (
                  <button type="button"
                    data-testid="btn-nuova-omologa"
                    onClick={() => setOmologaModal({ open: true, editing: null })}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-sage border border-sage rounded-md hover:bg-sage-light transition-colors">
                    <Plus size={12} />{t('anagrafiche.dett_produttore.nuova_omologa')}
                  </button>
                )}
              </div>
              {omologaDeleteError && (
                <div className="px-5 py-2 bg-accent-danger-bg border-b border-[#FECACA] text-sm text-accent-danger">{omologaDeleteError}</div>
              )}
              <div className="divide-y divide-stone-mid">
                {omologheLoading ? (
                  <div className="px-5 py-3 space-y-2">{[1, 2].map((i) => <SkeletonRow key={i} cols={4} />)}</div>
                ) : omologhe.length === 0 ? (
                  <div className="px-5 py-4 text-sm text-ink-soft italic">{t('anagrafiche.dett_produttore.nessuna_omologa')}</div>
                ) : (
                  omologhe.map((o) => (
                    <div key={o.id} data-testid="row-omologa"
                      className="flex items-start justify-between gap-4 px-5 py-3 hover:bg-stone-bg transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="text-sm font-medium text-ink-dark">{o.numero_omologa ?? t('anagrafiche.dett_produttore.omologa_sn')}</span>
                          <Badge variant={stateBadgeVariant(o.stato)}>{o.stato}</Badge>
                          <Badge variant={statoFirmaBadgeVariant(o.stato_firma)}>{o.stato_firma}</Badge>
                        </div>
                        <p className="text-xs text-ink-soft">
                          {t('anagrafiche.dett_produttore.firma_scad', { firma: formatDateIT(o.data_firma), scad: formatDateIT(o.data_scadenza) })}
                          {o.numero_conferimenti != null && t('anagrafiche.dett_produttore.conferimenti_suffix', { n: o.numero_conferimenti })}
                        </p>
                      </div>
                      {canEditOmologa && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button type="button" onClick={() => setOmologaModal({ open: true, editing: o })}
                            className="px-2.5 py-1 text-xs text-sage border border-sage rounded hover:bg-sage-light transition-colors">
                            {t('anagrafiche.dett_cliente.modifica')}
                          </button>
                          <button type="button" onClick={() => {
                              if (window.confirm(t('anagrafiche.dett_produttore.conferma_elimina_omologa'))) handleDeleteOmologa(o.id);
                            }}
                            className="px-2.5 py-1 text-xs text-accent-danger border border-[#FECACA] rounded hover:bg-accent-danger-bg transition-colors">
                            {t('anagrafiche.dett_cliente.elimina')}
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Ordini collegati */}
            <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-stone-mid bg-stone-bg">
                <span className="text-sm font-semibold text-ink-dark">
                  {t('anagrafiche.dett_cliente.ordini_collegati')} {!ordiniLoading && `(${ordini.length})`}
                </span>
              </div>
              <div className="divide-y divide-stone-mid">
                {ordiniLoading ? (
                  <div className="px-5 py-3 space-y-2">
                    {[1, 2].map((i) => <SkeletonRow key={i} />)}
                  </div>
                ) : ordini.length === 0 ? (
                  <div className="px-5 py-4 text-sm text-ink-soft italic">{t('anagrafiche.dett_cliente.nessun_ordine')}</div>
                ) : (
                  ordini.map((o) => (
                    <div
                      key={o.id}
                      onClick={() => navigate(`/richieste/${o.id}`)}
                      className="flex items-center justify-between px-5 py-3 hover:bg-stone-bg transition-colors cursor-pointer"
                    >
                      <div>
                        <p className="text-sm font-mono font-semibold text-ink-dark">{o.codice_ordine}</p>
                        <p className="text-xs text-ink-soft">{o.customer.ragione_sociale} · {categoriaLabel(o.categoria, categorie)}</p>
                      </div>
                      <StatusBadge status={o.status} />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* ── Colonna azioni ── */}
          <div className="flex-[1] min-w-[200px] space-y-3">
            {canEdit && !isEditing && (
              <button
                type="button"
                onClick={startEdit}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors"
              >
                <Pencil size={14} />
                {t('anagrafiche.dett_cliente.modifica')}
              </button>
            )}

            {canEdit && (
              <>
                {!showDeleteConfirm ? (
                  <button
                    type="button"
                    onClick={() => { setDeleteError(null); setShowDeleteConfirm(true); }}
                    disabled={isEditing}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-accent-danger border border-[#FECACA] rounded-lg hover:bg-accent-danger-bg transition-colors disabled:opacity-40"
                  >
                    <Trash2 size={14} />
                    {t('anagrafiche.dett_cliente.elimina')}
                  </button>
                ) : (
                  <div className="border border-[#FECACA] rounded-lg p-4 bg-accent-danger-bg">
                    <p className="text-sm text-ink-dark font-medium mb-1">{t('anagrafiche.dett_cliente.conferma_elimina')}</p>
                    <p className="text-xs text-ink-soft mb-3">{t('anagrafiche.dett_cliente.conferma_elimina_nota')}</p>
                    {deleteError && <p className="text-xs text-accent-danger mb-2">{deleteError}</p>}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="flex-1 px-3 py-2 text-xs font-medium text-white bg-accent-danger rounded-md hover:bg-[#B91C1C] transition-colors disabled:opacity-60"
                      >
                        {isDeleting ? t('anagrafiche.dett_cliente.eliminazione') : t('anagrafiche.dett_cliente.si_elimina')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                        className="flex-1 px-3 py-2 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors"
                      >
                        {t('common.azioni.annulla')}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            <button
              type="button"
              onClick={() => navigate('/produttori')}
              className="w-full px-4 py-2 text-sm text-ink-soft border border-stone-border rounded-lg hover:bg-stone-bg transition-colors"
            >
              {t('anagrafiche.dett_produttore.torna')}
            </button>
          </div>
        </div>
      </div>

      {omologaModal.open && id && (
        <OmologaModal
          produttoreId={id}
          produttoreEmail={produttore?.email ?? null}
          omologa={omologaModal.editing}
          onClose={() => {
            setOmologaModal({ open: false, editing: null });
            // refresha la lista per riflettere eventuali cambi stato_firma /
            // PDF generati durante la sessione del modal (sub-commit 4).
            fetchOmologhe();
          }}
          onSaved={(updated) => {
            // Aggiorna inline la riga corrispondente nella lista (refresh
            // ottimistico); il modal resta aperto per permettere all'utente
            // di lavorare sui Documenti omologa appena creata.
            setOmologhe((prev) => {
              const exists = prev.some((x) => x.id === updated.id);
              if (exists) return prev.map((x) => x.id === updated.id ? updated : x);
              return [updated, ...prev];
            });
            setOmologaModal((cur) => ({ open: cur.open, editing: updated }));
          }}
        />
      )}
    </div>
  );
}
